import type { HolidayNavTab, HolidaySection } from '@/src/types/holidays'

/**
 * The package page is read in a fixed order — what the trip is, what it costs, what to know before
 * booking — and several API sections belong to one block of that order. Inclusions and exclusions
 * are two sides of one answer; visa, payment, cancellation and terms are all small print.
 *
 * Blocks are built once and drive both the body and the nav tabs, so the tab bar cannot drift from
 * what is on the page. A section the API sends that no block claims still renders, in its original
 * position, as a block of its own — a new section type appears rather than disappearing.
 */

export type HolidayBlockKind = 'inclusions' | 'beforeYouBook' | 'section'

export interface HolidayPageBlock {
    /** Anchor id and nav target. For grouped blocks this is the block's own name. */
    id: string
    /** Heading above the block. */
    title?: string
    /** Tab label, which is shorter than the heading where a row of seven has to fit. */
    navLabel?: string
    kind: HolidayBlockKind
    /** In document order. A `section` block holds exactly one. */
    sections: HolidaySection[]
}

/** Section ids each grouped block absorbs, in the order they should appear inside it. */
const INCLUSION_IDS = ['inclusions', 'exclusions']
const BEFORE_YOU_BOOK_IDS = ['visa-requirements', 'notes', 'payment-terms', 'cancellation-policy', 'terms']

export const HOLIDAY_BLOCK_IDS = {
    INCLUSIONS: 'inclusions',
    BEFORE_YOU_BOOK: 'before-you-book'
} as const

interface BlockLabel {
    title: string
    navLabel: string
}

interface BuildOptions {
    /** Heading and tab label for each grouped block, already localised. */
    labels: { inclusions: BlockLabel; beforeYouBook: BlockLabel }
}

export function buildHolidayPageBlocks(
    sections: HolidaySection[],
    { labels }: BuildOptions
): HolidayPageBlock[] {
    const byId = new Map(sections.map(section => [section.id, section]))
    const claimed = new Set<string>()

    const collect = (ids: string[]): HolidaySection[] => {
        const found: HolidaySection[] = []
        for (const id of ids) {
            const section = byId.get(id)
            if (!section) continue
            found.push(section)
            claimed.add(id)
        }
        return found
    }

    const inclusionSections = collect(INCLUSION_IDS)
    const beforeSections = collect(BEFORE_YOU_BOOK_IDS)

    const blocks: HolidayPageBlock[] = []
    let inclusionsPlaced = false
    let beforePlaced = false

    for (const section of sections) {
        if (claimed.has(section.id)) {
            // A grouped block takes the position of the first of its sections.
            if (inclusionSections.includes(section) && !inclusionsPlaced) {
                inclusionsPlaced = true
                blocks.push({
                    id: HOLIDAY_BLOCK_IDS.INCLUSIONS,
                    title: labels.inclusions.title,
                    navLabel: labels.inclusions.navLabel,
                    kind: 'inclusions',
                    sections: inclusionSections
                })
            }
            if (beforeSections.includes(section) && !beforePlaced) {
                beforePlaced = true
                blocks.push({
                    id: HOLIDAY_BLOCK_IDS.BEFORE_YOU_BOOK,
                    title: labels.beforeYouBook.title,
                    navLabel: labels.beforeYouBook.navLabel,
                    kind: 'beforeYouBook',
                    sections: beforeSections
                })
            }
            continue
        }

        blocks.push({
            id: section.id,
            title: section.title,
            navLabel: section.navLabel ?? section.title,
            kind: 'section',
            sections: [section]
        })
    }

    return blocks
}

/** Nav tabs for the blocks that carry a label and are not hidden by the API. */
export function buildHolidayNavTabsFromBlocks(blocks: HolidayPageBlock[]): HolidayNavTab[] {
    return blocks
        .filter(block => {
            if (!block.navLabel) return false
            // `showInNav: false` only ever applies to a lone section; a grouped block is the page.
            if (block.kind === 'section' && block.sections[0].showInNav === false) return false
            return true
        })
        .map(block => ({ id: block.id, label: block.navLabel as string }))
}
